import React, {useContext, useMemo, useState} from 'react';
import {useSelector} from "react-redux";
import {
    Alert, Box, Card, CardContent, Chip, Stack,
    Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import {format} from "date-fns";
import {SnackBarContext} from "../../snackBarProvider";
import {
    useGetClanChatTOHMutation,
    useGetGlobalChatTOHMutation,
    useGetUserClanInfoTOHQuery,
} from "../../core/api/toh-clans-api";

// Both chat endpoints return the newest N of the range, so a small limit hides the start
// of the period. The global chat one has no upper bound at all - it is applied client-side.
const DEFAULT_LIMIT = 2000;

const csvCell = (value) => {
    const text = value == null ? '' : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

function ExportChatTOH() {
    const {addAlert} = useContext(SnackBarContext);

    const CommonData = useSelector((state) => state.data.CommonData);
    const stateId = typeof CommonData.deviceId === 'string' ? CommonData.deviceId : '';
    const stateIdIsEmpty = stateId === '' || stateId === '-';

    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [limit, setLimit] = useState('');
    const [loadedRange, setLoadedRange] = useState(null);

    const {data: clanInfo} = useGetUserClanInfoTOHQuery(stateId, {skip: stateIdIsEmpty});
    const clanId = clanInfo?.clan?.id || '';

    const [getClanChat, clanChatResponse] = useGetClanChatTOHMutation();
    const [getGlobalChat, globalChatResponse] = useGetGlobalChatTOHMutation();

    const isLoading = clanChatResponse.isLoading || globalChatResponse.isLoading;
    const nothingLoaded = loadedRange === null;

    const handleDateFromChange = (value) => {
        setDateFrom(value);
        if (value && dateTo && value > dateTo) setDateTo(value);
    };

    const handleDateToChange = (value) => {
        setDateTo(value && dateFrom && value < dateFrom ? dateFrom : value);
    };

    const handleLoad = async () => {
        const since = new Date(dateFrom + 'T00:00:00').getTime();
        const till = new Date(dateTo + 'T00:00:00').getTime() + 24 * 60 * 60 * 1000;
        const limitValue = limit ? parseInt(limit) : DEFAULT_LIMIT;

        const calls = [getGlobalChat({since, limit: limitValue})];
        if (clanId) {
            calls.push(getClanChat({clanId, since, till, limit: limitValue}));
        }
        const responses = await Promise.all(calls);

        const failed = responses.find((r) => r.hasOwnProperty('error'));
        if (failed) {
            addAlert({text: failed.error?.data?.message || 'Error loading chat', type: 'error'});
            return;
        }
        setLoadedRange({since, till, limit: limitValue});
    };

    const rows = useMemo(() => {
        if (!loadedRange) return [];
        const all = [
            ...(clanChatResponse.data?.messages || []),
            ...(globalChatResponse.data?.messages || []),
        ];
        const byId = new Map();
        all.forEach((msg) => {
            if (msg.type !== 'TEXT') return;
            if ((msg.userId || '').toLowerCase() !== stateId.toLowerCase()) return;
            // The global chat endpoint ignores `till`, so the upper bound is enforced here.
            if (msg.createdAt < loadedRange.since || msg.createdAt >= loadedRange.till) return;
            byId.set(msg.id, msg);
        });
        return Array.from(byId.values()).sort((a, b) => a.createdAt - b.createdAt);
    }, [clanChatResponse.data, globalChatResponse.data, loadedRange, stateId]);

    const handleExportCsv = () => {
        const header = 'created_at,chat_type,chat_id,user_id,text';
        const body = rows.map((msg) => [
            new Date(msg.createdAt).toISOString(),
            msg.chatType,
            msg.chatId,
            msg.userId,
            msg.customData?.text,
        ].map(csvCell).join(','));

        // BOM keeps Excel from mangling non-latin text.
        const blob = new Blob(['﻿' + [header, ...body].join('\n')], {
            type: 'text/csv;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `chat_${stateId}_${dateFrom}_${dateTo}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (stateIdIsEmpty) {
        return <Alert severity="info">Specify a state id to export chat messages</Alert>;
    }

    return (
        <>
            <h2>Export chat for {stateId}</h2>
            <Card>
                <CardContent>
                    <Stack direction="row" spacing={1} sx={{alignItems: "center", mb: 2, flexWrap: "wrap"}}>
                        <TextField
                            label="From" size="small" type="date"
                            InputLabelProps={{shrink: true}} value={dateFrom}
                            onChange={(e) => handleDateFromChange(e.target.value)}
                        />
                        <TextField
                            label="To" size="small" type="date"
                            InputLabelProps={{shrink: true}} value={dateTo}
                            slotProps={{htmlInput: {min: dateFrom}}}
                            onChange={(e) => handleDateToChange(e.target.value)}
                        />
                        <TextField
                            label="Limit" size="small" type="number" sx={{width: 120}} value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            placeholder={String(DEFAULT_LIMIT)}
                        />
                        <LoadingButton
                            loading={isLoading}
                            variant="outlined" color="info"
                            startIcon={<SearchIcon/>}
                            disabled={!dateFrom || !dateTo}
                            onClick={handleLoad}>
                            Load
                        </LoadingButton>
                        <LoadingButton
                            variant="outlined" color="success"
                            startIcon={<DownloadIcon/>}
                            disabled={rows.length === 0}
                            onClick={handleExportCsv}>
                            Export to CSV
                        </LoadingButton>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{alignItems: "center", mb: 2}}>
                        <Typography variant="caption" sx={{color: "text.secondary"}}>
                            {clanId
                                ? `Clan chat (${clanId}) + global chat, TEXT messages of this player only`
                                : 'Player is not in a clan - global chat only, TEXT messages of this player only'}
                        </Typography>
                        {!nothingLoaded && (
                            <Chip size="small" variant="outlined" label={`${rows.length} messages`}/>
                        )}
                    </Stack>

                    {rows.length > 0 ? (
                        <Box sx={{maxHeight: 600, overflowY: 'auto'}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Chat</TableCell>
                                        <TableCell>Chat ID</TableCell>
                                        <TableCell>Text</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((msg) => (
                                        <TableRow key={msg.id}>
                                            <TableCell sx={{whiteSpace: 'nowrap'}}>
                                                {format(msg.createdAt, "dd.MM.yyyy HH:mm:ss")}
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" variant="outlined" label={msg.chatType}/>
                                            </TableCell>
                                            <TableCell sx={{wordBreak: 'break-all'}}>{msg.chatId}</TableCell>
                                            <TableCell sx={{wordBreak: 'break-word'}}>
                                                {msg.customData?.text}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <Typography sx={{color: "text.secondary"}}>
                            {nothingLoaded
                                ? 'Choose a period and click "Load"'
                                : 'No text messages from this player for the selected period'}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </>
    );
}

export default ExportChatTOH
